import React, { useMemo, lazy, Suspense, useState, useEffect } from 'react';
import SEO from '../common/SEO';
import Hero from './Hero';


const SocialShareBar = lazy(() => import('../ui/SocialShareBar'));
const ComposerModeSection = lazy(() => import('./ComposerModeSection'));
const SoundWithoutLimits = lazy(() => import('./SoundWithoutLimits'));
const HowItWorks = lazy(() => import('./HowItWorks'));
const FreePlaySection = lazy(() => import('./FreePlaySection'));
const Features = lazy(() => import('./Features'));
const InteractionModesSection = lazy(() => import('./InteractionModesSection'));
const AtmosphereSection = lazy(() => import('./AtmosphereSection'));
const PlayModes = lazy(() => import('../ui/PlayModes'));
const FAQ = lazy(() => import('./FAQ'));
const CTA = lazy(() => import('./CTA'));
const Footer = lazy(() => import('./Footer'));
const BackToTop = lazy(() => import('../ui/BackToTop'));


interface LandingScreenProps {
  onStartAudio: () => void;
}

const LandingScreen: React.FC<LandingScreenProps> = ({ onStartAudio }) => {


  const landingSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": "https://solunn.com/#website",
        "url": "https://solunn.com/",
        "name": "Solunn",
        "description": "Free ambient soundscape generator for meditation, focus, and creative relaxation",
        "publisher": {
          "@id": "https://solunn.com/#organization"
        }
      },
      {
        "@type": "Organization",
        "@id": "https://solunn.com/#organization",
        "name": "Solunn",
        "url": "https://solunn.com/",
        "logo": {
          "@type": "ImageObject",
          "url": "https://solunn.com/og-image.png"
        },
        "sameAs": []
      },
      {
        "@type": "SoftwareApplication",
        "@id": "https://solunn.com/#app",
        "name": "Solunn - Ambient Soundscape Generator",
        "url": "https://solunn.com/",
        "applicationCategory": ["MultimediaApplication", "HealthApplication", "MusicApplication"],
        "operatingSystem": "Web Browser (Chrome, Firefox, Safari, Edge)",
        "description": "A free browser-based creative meditation tool for crafting calming ambient soundscapes. Create personalized relaxation sounds, focus music, and generative audio experiences without downloads or sign-ups. Perfect for meditation, deep work, sleep, and mindful sound exploration.",
        "keywords": "ambient soundscape generator, creative meditation app, relaxation sounds, focus music, generative music, sound meditation, calming audio, mindfulness tool, free meditation app, browser synthesizer",
        "screenshot": "https://solunn.com/og-image.png",
        "featureList": [
          "Multi-touch sound synthesis",
          "3D particle visualizations",
          "Reverb and delay effects",
          "Multiple waveform types",
          "Classic melody play modes",
          "Works on desktop and mobile"
        ],
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Solunn",
            "item": "https://solunn.com/"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Is Solunn free to use?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Completely free. Forever. Runs entirely in your browser—no downloads, no sign-ups, no strings attached."
            }
          },
          {
            "@type": "Question",
            "name": "Can I use Solunn for meditation?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolutely. Many users find Solunn perfect for creating personalized meditation soundscapes. The gentle, exploratory nature of the interface naturally invites a mindful, present state."
            }
          },
          {
            "@type": "Question",
            "name": "Is Solunn good for focus and deep work?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes! Create ambient soundscapes tailored to your focus needs—whether you prefer warm drones, gentle arpeggios, or evolving textures. It's like having a personal ambient music studio in your browser."
            }
          },
          {
            "@type": "Question",
            "name": "How is Solunn different from Calm or Headspace?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Solunn isn't a guided meditation app—it's a creative instrument for making your own relaxation soundscapes. Instead of passive listening, you're actively engaged in a calming, creative process."
            }
          },
          {
            "@type": "Question",
            "name": "What devices does Solunn work on?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Solunn works on any modern browser with Web Audio API support—Chrome, Firefox, Safari, and Edge on desktop, tablet, or mobile. No app download required."
            }
          }
        ]
      }
    ]
  }), []);

  return (
    <div className="bg-black text-white overflow-x-hidden">
      <SEO
        title="Solunn - Creative Meditation & Ambient Soundscape Generator"
        description="Breathe. Touch. Create. A serene space for crafting calming soundscapes and ambient audio. Free creative meditation tool for relaxation, focus, and mindful sound exploration."
        schema={landingSchema}
      />

      <main>
        <Hero onStartAudio={onStartAudio} />

        <Suspense fallback={<div className="h-20 bg-black" />}>
          <div className="relative z-20 pt-32 pb-12">
            <SocialShareBar />
          </div>

          {/* Composer Mode Hero Section - Prominent placement */}
          <ComposerModeSection />

          {/* SEO Content Sections */}
          <div className="relative">
            <div className="bg-gradient-to-b from-black via-zinc-900 to-black">
              <SoundWithoutLimits />
            </div>

            <HowItWorks />

            <div className="bg-gradient-to-b from-black via-zinc-900 to-black">
              <Features />
            </div>

            {/* Interaction Modes Explainer */}
            <InteractionModesSection />

            {/* Atmosphere Features */}
            <AtmosphereSection />

            {/* Variety First */}
            <PlayModes />

            {/* Free Play Deep Dive */}
            <FreePlaySection />

            <div className="bg-gradient-to-b from-black via-zinc-900 to-black">
              <FAQ />
            </div>

            <CTA onStartAudio={onStartAudio} />

            {/* Lower Share Bar with plenty of space */}
            <div
              className="relative z-20 py-32 md:py-48"
            >
              <SocialShareBar />
            </div>
          </div>
        </Suspense>
      </main>

      <Suspense fallback={<div className="h-20 bg-black" />}>
        <Footer />
      </Suspense>

      <Suspense fallback={null}>
        <BackToTop />
      </Suspense>
    </div>
  );
};

export default LandingScreen;
